import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box, SimpleGrid, useColorModeValue, Text, Icon, Flex, Badge, Divider } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from 'components/card/Card';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import {
    MdComputer, MdBuild, MdAttachMoney, MdWarning,
    MdTimer, MdInventory, MdApartment, MdBarChart, MdFingerprint,
    MdDeleteForever
} from 'react-icons/md';
import resguardosApi from 'api/resguardos';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, AreaChart, Area, CartesianGrid, Legend, LabelList
} from 'recharts';

// --- CONFIGURACIÓN VISUAL ---
const COLORS = ['#4318FF', '#01B574', '#FFB547', '#EE5D50', '#6AD2FF', '#7551FF', '#90CDF4', '#F6AD55', '#A0AEC0', '#ED64A6'];

// Traduce el Abrev de tbaHoteles (guardado en sessionStorage) al Id numérico que espera /historial/:idHotel
const HOTEL_MAP: Record<string, number> = {
    GVRN: 1, GVRM: 2, GVBLC: 3, GVLC: 4, VV: 5, CV: 6, MDC: 7, CC: 8, DEMO: 9,
};

const truncate = (str: string, n: number) => (str.length > n ? str.substr(0, n - 1) + '...' : str);

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <Box bg="white" p="12px" border="1px solid #E2E8F0" borderRadius="12px" boxShadow="xl">
                <Text fontWeight="bold" color="secondaryGray.900" mb="5px">{label}</Text>
                {payload.map((entry: any, index: number) => (
                    <Flex key={index} align="center">
                        <Box w="10px" h="10px" borderRadius="50%" bg={entry.color || entry.fill} mr="8px" />
                        <Text color="secondaryGray.600" fontSize="sm">
                            {entry.name === 'Inversión' ? `Inversión: $${entry.value.toLocaleString()}` :
                             entry.name === 'Días' ? `${entry.value} días restantes` :
                             `${entry.value} unidades`}
                        </Text>
                    </Flex>
                ))}
            </Box>
        );
    }
    return null;
};

// --- LOADING SCREEN ---
const BounceDot = ({ delay }: { delay: number }) => (
    <motion.span
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 0.65, repeat: Infinity, delay, ease: 'easeInOut' }}
        style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#7551FF' }}
    />
);

function DashboardLoader() {
    return (
        <motion.div
            key="dashboard-loader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'linear-gradient(160deg, #fafbff 0%, #ffffff 55%, #f3f0ff 100%)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 20,
                userSelect: 'none', pointerEvents: 'none',
            }}
        >
            {/* Icono central con anillos */}
            <div style={{ position: 'relative', width: 96, height: 96 }}>
                <motion.div
                    animate={{ scale: [1, 1.45, 1], opacity: [0.18, 0, 0.18] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ position: 'absolute', inset: -22, borderRadius: '50%', border: '2px solid #4318FF' }}
                />
                <motion.div
                    animate={{ scale: [1, 1.2, 1], opacity: [0.32, 0.04, 0.32] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                    style={{ position: 'absolute', inset: -9, borderRadius: '50%', border: '2px solid #7551FF' }}
                />
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
                    style={{
                        position: 'absolute', inset: 0, borderRadius: '50%',
                        background: 'conic-gradient(transparent 55%, #4318FF 100%)',
                        opacity: 0.45,
                    }}
                />
                <div style={{
                    position: 'absolute', inset: 3, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #4318FF 0%, #7551FF 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(67, 24, 255, 0.35)',
                }}>
                    <MdInventory size={42} color="white" />
                </div>
            </div>

            {/* Título y subtítulo */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.5 }}
                style={{ textAlign: 'center' }}
            >
                <p style={{ fontSize: 22, fontWeight: 700, color: '#1A202C', margin: 0, marginBottom: 10 }}>
                    Sistema de Inventarios
                </p>
                <div style={{ fontSize: 14, color: '#718096', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <span>Cargando datos</span>
                    <div style={{ display: 'flex', gap: 5 }}>
                        <BounceDot delay={0} />
                        <BounceDot delay={0.15} />
                        <BounceDot delay={0.3} />
                    </div>
                </div>
            </motion.div>

            {/* Barra de progreso inferior */}
            <motion.div
                initial={{ width: '0%' }}
                animate={{ width: ['0%', '55%', '78%', '90%'] }}
                transition={{ duration: 1.9, times: [0, 0.35, 0.7, 1], ease: 'easeOut' }}
                style={{
                    position: 'absolute', bottom: 0, left: 0,
                    height: 3,
                    background: 'linear-gradient(90deg, #4318FF, #7551FF, #6AD2FF)',
                    borderRadius: '0 2px 0 0',
                }}
            />
        </motion.div>
    );
}

// --- VARIANTES DE ANIMACIÓN DE CARDS ---
const cardVariants = {
    hidden: { opacity: 0, y: 22 },
    visible: (i: number) => ({
        opacity: 1, y: 0,
        transition: { duration: 0.5, delay: 0.2 + i * 0.07, ease: [0.16, 1, 0.3, 1] }
    })
};

const MotionBox = motion(Box) as any;

export default function DashboardActivosVelas() {
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const gridColor = useColorModeValue('#E2E8F0', '#2D3748');

    const [loading, setLoading] = useState(true);
    const isFirstLoad = useRef(true);
    const [resguardosRemota, setResguardosRemota] = useState<any[]>([]);
    const [bajasGrupos, setBajasGrupos] = useState<any[]>([]);

    const [stats, setStats] = useState({
        activosRaw: [] as any[],
        topModelos: [],
        porNombreExacto: [],
        porDepartamento: [],
        inversionSublinea: [],
        porEstado: [],
        datosMensuales: [],
        vencimientosDetalle: [],
        proximasGarantias: 0,
        enMantenimiento: 0,
    });

    const fetchData = async () => {
        try {
            // necesitamos un timeout porque a veces el cambio de hotel y la actualización del sessionStorage no son instantáneos
            await new Promise(res => setTimeout(res, 500));

            const hotel = sessionStorage.getItem('hotelSeleccionado') || 'GVRN';
            const usuarioId = JSON.parse(sessionStorage.getItem('user') || '{}')?.Id;
            const res = await fetch(`https://inventariosbackend.velasresorts.com/inventarios/activos/listado/hotelesdashboard/${hotel}?usuarioId=${usuarioId}`);
            const data = (await res.json()).flat();

            setStats({
                activosRaw: data,
                topModelos: getTopItems(data, 'Modelo', 6),
                porNombreExacto: getTopItems(data, 'ActiveName', 10),
                porDepartamento: getTopItems(data, 'Departamento', 6),
                inversionSublinea: getInvestmentBy(data, 'tbaSublinea'),
                porEstado: getTopItems(data, 'Estado', 5),
                datosMensuales: getMonthlyGrowth(data),
                vencimientosDetalle: getExpiringAssets(data, 5),
                proximasGarantias: data.filter((a: any) => {
                    if (!a.Garantia) return false;
                    const diff = new Date(a.Garantia).getTime() - new Date().getTime();
                    return diff > 0 && diff < (1000 * 60 * 60 * 24 * 90);
                }).length,
                enMantenimiento: data.filter((a: any) => a.Estado === 'in_maintenance').length,
            });
        } catch (e) {
            console.error("Error cargando dashboard:", e);
        } finally {
            if (isFirstLoad.current) {
                isFirstLoad.current = false;
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchData();
        window.addEventListener('hotelCambiado', fetchData);
        return () => window.removeEventListener('hotelCambiado', fetchData);
    }, []);

    useEffect(() => {
        const fetchResguardos = async () => {
            try {
                const userData = JSON.parse(sessionStorage.getItem("user") || "{}");
                const hotelIds = userData.HotelIds || [];
                const res = await resguardosApi.listadoId(hotelIds);
                const lista = Array.isArray(res) ? res : [];
                setResguardosRemota(
                    lista.filter((r: any) =>
                        r.status === "Pendiente de firma del colaborador" ||
                        r.status === "Recepción confirmada"
                    )
                );
            } catch (e) {
                console.error("Error cargando resguardos remotos:", e);
            }
        };
        fetchResguardos();
        window.addEventListener('hotelCambiado', fetchResguardos);
        return () => window.removeEventListener('hotelCambiado', fetchResguardos);
    }, []);

    useEffect(() => {
        const fetchBajas = async () => {
            try {
                const hotelAbrev = sessionStorage.getItem('hotelSeleccionado') || 'GVRN';
                const hotelId = HOTEL_MAP[hotelAbrev] || 1;
                const res = await fetch(`https://inventariosbackend.velasresorts.com/inventarios/historial/${hotelId}`);
                const data = await res.json();
                setBajasGrupos(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Error cargando bajas:", e);
                setBajasGrupos([]);
            }
        };
        fetchBajas();
        window.addEventListener('hotelCambiado', fetchBajas);
        return () => window.removeEventListener('hotelCambiado', fetchBajas);
    }, []);

    const totalInversion = useMemo(() =>
        stats.activosRaw.reduce((a, b) => a + (parseFloat(b.Precio) || 0), 0),
    [stats.activosRaw]);

    const pendientesRemota = useMemo(
        () => resguardosRemota.filter(r => r.status === "Pendiente de firma del colaborador"),
        [resguardosRemota]
    );

    const totalActivosBaja = useMemo(
        () => bajasGrupos.reduce((acc, g) => acc + (g.cantidadActivos || 0), 0),
        [bajasGrupos]
    );

    const bajasEsteMes = useMemo(() => {
        const hoy = new Date();
        return bajasGrupos
            .filter(g => {
                if (!g.fecha_baja) return false;
                const f = new Date(g.fecha_baja);
                return f.getFullYear() === hoy.getFullYear() && f.getMonth() === hoy.getMonth();
            })
            .reduce((acc, g) => acc + (g.cantidadActivos || 0), 0);
    }, [bajasGrupos]);

    const bajasRecientes = useMemo(
        () => [...bajasGrupos]
            .sort((a, b) => new Date(b.fecha_baja).getTime() - new Date(a.fecha_baja).getTime())
            .slice(0, 5),
        [bajasGrupos]
    );

    const anim = loading ? "hidden" : "visible";

    return (
        <>
            <AnimatePresence>
                {loading && <DashboardLoader />}
            </AnimatePresence>

            <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px="20px">
                {/* KPI Row */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 6 }} spacing="20px" mb="20px">
                    <MotionBox variants={cardVariants} custom={0} initial="hidden" animate={anim}>
                        <Card p="15px">
                            <MiniStatistics
                                name="Valor de Inventario"
                                value={`$${totalInversion.toLocaleString()}`}
                                startContent={<IconBox w="56px" h="56px" bg="green.100" icon={<Icon as={MdAttachMoney} color="green.500" w="28px" h="28px"/>}/>}
                            />
                        </Card>
                    </MotionBox>
                    <MotionBox variants={cardVariants} custom={1} initial="hidden" animate={anim}>
                        <Card p="15px">
                            <MiniStatistics
                                name="Activos Totales"
                                value={stats.activosRaw.length}
                                startContent={<IconBox w="56px" h="56px" bg="brand.100" icon={<Icon as={MdComputer} color="brand.500" w="28px" h="28px"/>}/>}
                            />
                        </Card>
                    </MotionBox>
                    <MotionBox variants={cardVariants} custom={2} initial="hidden" animate={anim}>
                        <Card p="15px">
                            <MiniStatistics
                                name="Garantías Críticas"
                                value={stats.proximasGarantias}
                                startContent={<IconBox w="56px" h="56px" bg="orange.100" icon={<Icon as={MdWarning} color="orange.500" w="28px" h="28px"/>}/>}
                            />
                        </Card>
                    </MotionBox>
                    <MotionBox variants={cardVariants} custom={3} initial="hidden" animate={anim}>
                        <Card p="15px">
                            <MiniStatistics
                                name="En Mantenimiento"
                                value={stats.enMantenimiento}
                                startContent={<IconBox w="56px" h="56px" bg="red.100" icon={<Icon as={MdBuild} color="red.500" w="28px" h="28px"/>}/>}
                            />
                        </Card>
                    </MotionBox>
                    <MotionBox variants={cardVariants} custom={4} initial="hidden" animate={anim}>
                        <Card p="15px">
                            <MiniStatistics
                                name="Firma Remota Pendiente"
                                value={pendientesRemota.length}
                                startContent={<IconBox w="56px" h="56px" bg="yellow.100" icon={<Icon as={MdFingerprint} color="yellow.500" w="28px" h="28px"/>}/>}
                            />
                        </Card>
                    </MotionBox>
                    <MotionBox variants={cardVariants} custom={5} initial="hidden" animate={anim}>
                        <Card p="15px">
                            <MiniStatistics
                                name="Bajas Registradas"
                                value={totalActivosBaja}
                                startContent={<IconBox w="56px" h="56px" bg="red.100" icon={<Icon as={MdDeleteForever} color="red.500" w="28px" h="28px"/>}/>}
                            />
                        </Card>
                    </MotionBox>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="20px">

                    {/* 0. Resguardos Firma Remota Pendientes */}
                    <MotionBox variants={cardVariants} custom={4} initial="hidden" animate={anim} gridColumn={{ xl: "span 3" }}>
                        <Card p="20px">
                            <Flex align="center" mb="16px" justify="space-between" wrap="wrap" gap="2">
                                <Flex align="center">
                                    <Icon as={MdFingerprint} color="yellow.500" mr="10px" w="22px" h="22px" />
                                    <Text color={textColor} fontSize="md" fontWeight="700">Resguardos Pendientes de Firma Remota</Text>
                                </Flex>
                                <Badge colorScheme="yellow" px="3" py="1" borderRadius="md" fontSize="sm">
                                    {pendientesRemota.length} pendientes
                                </Badge>
                            </Flex>
                            <Divider mb="12px" />
                            {pendientesRemota.length === 0 ? (
                                <Flex justify="center" align="center" py="24px">
                                    <Text color="gray.400" fontSize="sm">No hay firmas remotas pendientes</Text>
                                </Flex>
                            ) : (
                                <Box overflowY="auto" maxH="260px">
                                    {[...pendientesRemota]
                                        .sort((a, b) =>
                                            new Date(a.LastModifiedDate || a.CreatedDate).getTime() -
                                            new Date(b.LastModifiedDate || b.CreatedDate).getTime()
                                        )
                                        .map((r, i) => (
                                            <Flex
                                                key={r.ResguardoId || i}
                                                align="center"
                                                py="10px"
                                                borderBottom="1px solid"
                                                borderColor={gridColor}
                                                _last={{ borderBottom: 'none' }}
                                                gap="12px"
                                            >
                                                <Box w="10px" h="10px" borderRadius="full" bg="yellow.400" flexShrink={0} />
                                                <Box flex="1" minW="0">
                                                    <Text fontWeight="600" fontSize="sm" noOfLines={1}>{r.usuarioDueno}</Text>
                                                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                                        {r.Departamento} • N°{r.usuarioNumColab}
                                                    </Text>
                                                </Box>
                                                <Text fontSize="xs" color="gray.400" flexShrink={0} display={{ base: 'none', md: 'block' }}>
                                                    {new Date(r.LastModifiedDate || r.CreatedDate).toLocaleDateString()}
                                                </Text>
                                                <Badge colorScheme="yellow" px="2" py="1" borderRadius="md" fontSize="xs" flexShrink={0}>
                                                    Pendiente
                                                </Badge>
                                            </Flex>
                                        ))
                                    }
                                </Box>
                            )}
                        </Card>
                    </MotionBox>

                    {/* 0.5 Bajas Recientes */}
                    <MotionBox variants={cardVariants} custom={5} initial="hidden" animate={anim}>
                        <Card p="20px">
                            <Flex align="center" mb="16px" justify="space-between" wrap="wrap" gap="2">
                                <Flex align="center">
                                    <Icon as={MdDeleteForever} color="red.400" mr="10px" w="22px" h="22px" />
                                    <Text color={textColor} fontSize="md" fontWeight="700">Bajas Recientes</Text>
                                </Flex>
                                <Badge colorScheme="red" px="3" py="1" borderRadius="md" fontSize="sm">
                                    {bajasEsteMes} este mes
                                </Badge>
                            </Flex>
                            <Divider mb="12px" />
                            {bajasRecientes.length === 0 ? (
                                <Flex justify="center" align="center" py="24px">
                                    <Text color="gray.400" fontSize="sm">No hay bajas registradas</Text>
                                </Flex>
                            ) : (
                                <Box overflowY="auto" maxH="260px">
                                    {bajasRecientes.map((grupo, i) => (
                                        <Flex
                                            key={grupo.descargaId || i}
                                            align="center"
                                            py="10px"
                                            borderBottom="1px solid"
                                            borderColor={gridColor}
                                            _last={{ borderBottom: 'none' }}
                                            gap="12px"
                                        >
                                            <Box w="10px" h="10px" borderRadius="full" bg="red.400" flexShrink={0} />
                                            <Box flex="1" minW="0">
                                                <Text fontWeight="600" fontSize="sm" noOfLines={1}>
                                                    {grupo.cantidadActivos} activo{grupo.cantidadActivos !== 1 ? 's' : ''} • {grupo.motivos?.[0] || 'Sin motivo'}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                                    Responsable: {grupo.responsable}
                                                </Text>
                                            </Box>
                                            <Text fontSize="xs" color="gray.400" flexShrink={0} display={{ base: 'none', md: 'block' }}>
                                                {grupo.fecha_baja ? new Date(grupo.fecha_baja).toLocaleDateString('es-MX', { timeZone: 'UTC' }) : '—'}
                                            </Text>
                                        </Flex>
                                    ))}
                                </Box>
                            )}
                        </Card>
                    </MotionBox>

                    {/* 1. Crecimiento Mensual */}
                    <MotionBox variants={cardVariants} custom={4} initial="hidden" animate={anim} gridColumn={{ xl: "span 2" }}>
                        <Card p="20px">
                            <Text color={textColor} fontSize="lg" fontWeight="700" mb="20px">Crecimiento Histórico de Activos</Text>
                            <Box h="300px">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={stats.datosMensuales} margin={{ left: 0, right: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />
                                        <XAxis dataKey="month" fontSize={12} axisLine={false} tickLine={false} />
                                        <YAxis fontSize={12} axisLine={false} tickLine={false} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Area type="monotone" dataKey="cantidad" name="Cantidad" stroke={COLORS[0]} strokeWidth={3} fillOpacity={0.1} fill={COLORS[0]} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                    </MotionBox>

                    {/* 2. Estado de la Flota */}
                    <MotionBox variants={cardVariants} custom={5} initial="hidden" animate={anim}>
                        <Card p="20px">
                            <Text color={textColor} fontSize="lg" fontWeight="700" mb="20px">Estado Operativo</Text>
                            <Box h="300px">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={stats.porEstado} innerRadius={70} outerRadius={90} paddingAngle={5} dataKey="value">
                                            {stats.porEstado.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                        <Legend wrapperStyle={{fontSize: '12px'}} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                    </MotionBox>

                    {/* 3. Inventario por Nombre */}
                    <MotionBox variants={cardVariants} custom={6} initial="hidden" animate={anim}>
                        <Card p="20px">
                            <Flex align="center" mb="20px">
                                <Icon as={MdInventory} color="brand.500" mr="10px" />
                                <Text color={textColor} fontSize="md" fontWeight="700">Inventario por Tipo</Text>
                            </Flex>
                            <Box h="350px">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stats.porNombreExacto} layout="vertical" margin={{ left: 5, right: 45 }}>
                                        <XAxis type="number" hide />
                                        <YAxis
                                            dataKey="name" type="category" fontSize={12} width={120}
                                            tickFormatter={(value) => truncate(value, 20)} axisLine={false} tickLine={false}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="value" name="unidades" fill={COLORS[5]} radius={[0, 10, 10, 0]} barSize={20}>
                                            <LabelList dataKey="value" position="right" fontSize={12} fontWeight="bold" fill={textColor} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                    </MotionBox>

                    {/* 4. Garantías Críticas */}
                    <MotionBox variants={cardVariants} custom={7} initial="hidden" animate={anim}>
                        <Card p="20px">
                            <Flex align="center" mb="20px">
                                <Icon as={MdTimer} color="red.400" mr="10px" />
                                <Text color={textColor} fontSize="md" fontWeight="700">Garantías por Vencer</Text>
                            </Flex>
                            <Box h="350px">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stats.vencimientosDetalle} layout="vertical" margin={{ left: 5, right: 55 }}>
                                        <XAxis type="number" hide />
                                        <YAxis
                                            dataKey="name" type="category" fontSize={11} width={130}
                                            tickFormatter={(value) => truncate(value, 20)} axisLine={false} tickLine={false}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="days" name="Días" fill="#EE5D50" radius={[0, 10, 10, 0]} barSize={18}>
                                            <LabelList dataKey="days" position="right" fontSize={11} fontWeight="bold" formatter={(v:any) => `${v}d`} fill={textColor} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                    </MotionBox>

                    {/* 5. Ubicación por Departamento */}
                    <MotionBox variants={cardVariants} custom={8} initial="hidden" animate={anim}>
                        <Card p="20px">
                            <Flex align="center" mb="20px">
                                <Icon as={MdApartment} color="purple.500" mr="10px" />
                                <Text color={textColor} fontSize="md" fontWeight="700">Por Departamento</Text>
                            </Flex>
                            <Box h="350px">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stats.porDepartamento} layout="vertical" margin={{ left: 5, right: 45 }}>
                                        <XAxis type="number" hide />
                                        <YAxis
                                            dataKey="name" type="category" fontSize={12} width={110}
                                            tickFormatter={(value) => truncate(value, 18)} axisLine={false} tickLine={false}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="value" name="unidades" fill={COLORS[6]} radius={[0, 10, 10, 0]} barSize={20}>
                                            <LabelList dataKey="value" position="right" fontSize={12} fontWeight="bold" fill={textColor} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                    </MotionBox>

                    {/* 6. Inversión por Sublínea */}
                    <MotionBox variants={cardVariants} custom={9} initial="hidden" animate={anim}>
                        <Card p="20px">
                            <Text color={textColor} fontSize="md" fontWeight="700" mb="20px">Inversión por Categoría</Text>
                            <Box h="300px">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stats.inversionSublinea} margin={{ top: 25 }}>
                                        <XAxis dataKey="name" fontSize={11} axisLine={false} tickLine={false} tickFormatter={(v) => truncate(v, 12)} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="value" name="Inversión" fill={COLORS[1]} radius={[10, 10, 0, 0]}>
                                            <LabelList dataKey="value" position="top" formatter={(v: any) => `$${(v/1000).toFixed(0)}k`} fontSize={11} fontWeight="bold" fill={textColor} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                    </MotionBox>

                    {/* 7. Modelos Predominantes */}
                    <MotionBox variants={cardVariants} custom={10} initial="hidden" animate={anim} gridColumn={{ xl: "span 2" }}>
                        <Card p="20px">
                            <Flex align="center" mb="20px">
                                <Icon as={MdBarChart} color="blue.400" mr="10px" />
                                <Text color={textColor} fontSize="md" fontWeight="700">Modelos Predominantes</Text>
                            </Flex>
                            <Box h="300px">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stats.topModelos} layout="vertical" margin={{ left: 10, right: 45 }}>
                                        <XAxis type="number" hide />
                                        <YAxis
                                            dataKey="name" type="category" fontSize={12} width={160}
                                            tickFormatter={(value) => truncate(value, 28)} axisLine={false} tickLine={false}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="value" name="unidades" fill={COLORS[4]} radius={[0, 10, 10, 0]} barSize={18}>
                                            <LabelList dataKey="value" position="right" fontSize={12} fontWeight="bold" fill={textColor} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                    </MotionBox>

                </SimpleGrid>
            </Box>
        </>
    );
}

// --- HELPERS DE PROCESAMIENTO ---

const getTopItems = (data: any[], key: string, limit = 5) => {
    const counts: Record<string, number> = {};

    data.forEach(item => {
        if (item[key] && item[key].toString().trim() !== "") {
            const val = item[key].toString().trim().toUpperCase();
            counts[val] = (counts[val] || 0) + 1;
        }
    });

    return Object.entries(counts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, limit);
};

const getExpiringAssets = (data: any[], limit = 5) => {
    const hoy = new Date();
    return data
        .filter(item => item.Garantia)
        .map(item => {
            const fechaGarantia = new Date(item.Garantia);
            const diffDays = Math.ceil((fechaGarantia.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
            const assetName = item.ActiveName ? item.ActiveName.toString().trim().toUpperCase() : 'ACTIVO';
            return {
                name: `${assetName} (${item.Folio || 'S/F'})`,
                days: diffDays
            };
        })
        .filter(item => item.days > 0)
        .sort((a, b) => a.days - b.days)
        .slice(0, limit);
};

const getMonthlyGrowth = (data: any[]) => {
    const months: Record<string, number> = {};
    const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    const currentYear = new Date().getFullYear();
    for(let i=0; i <= new Date().getMonth(); i++) {
        months[`${currentYear}-${String(i+1).padStart(2, '0')}`] = 0;
    }
    data.forEach(item => {
        if (!item.CreatedDate) return;
        const date = new Date(item.CreatedDate);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (months[key] !== undefined) months[key]++;
    });
    return Object.entries(months).map(([key, cantidad]) => ({
        month: monthNames[parseInt(key.split('-')[1])-1],
        cantidad,
        fullDate: key
    })).sort((a, b) => a.fullDate.localeCompare(b.fullDate));
};

const getInvestmentBy = (data: any[], key: string) => {
    const totals: Record<string, number> = {};
    data.forEach(item => {
        const val = parseFloat(item.Precio) || 0;
        const category = item[key] ? item[key].toString().trim().toUpperCase() : 'OTROS';
        totals[category] = (totals[category] || 0) + val;
    });
    return Object.entries(totals)
        .map(([name, value]) => ({ name, value: Math.round(value) }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 6);
};
